import { useCallback, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { validateString } from 'src/utils/string/stringUtil';
import { InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import {
	deleteMST2020_R01,
	getMST2020_R01AllCount,
	getMST2020_R01List,
	patchMST2020_R01,
	postMST2020_R01,
} from './Api_MST2020_R01';
import {
	defaultMST2020_R01Dto,
	MST2020_R01Dto,
} from './Dto_MST2020_R01';
import {
	MST2020_R01Filter,
	useMST2020_R01Store,
} from './Store_MST2020_R01';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST2020_R01Dto> | null>;
	setMessage: (message: string) => void;
}

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const Content_MST2020_R01 = ({ setMessage }: Props) => {
	const {
		MST2020_R01Filter,
		MST2020_R01PastFilter,
		setMST2020_R01PastFilter,
		MST2020_R01List,
		setMST2020_R01List,
		insertMST2020_R01List,
		deletedMST2020_R01List,
		addDeletedMST2020_R01List,
		removeDeletedMST2020_R01List,
		selectedMST2020_R01List,
		setSelectedMST2020_R01List,
		removeSelectedMST2020_R01List,
		setOriginalMST2020_R01List,
		setAllCount,
		setScrollTop,
	} = useMST2020_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const [clickable, setClickable] = useState(true);

	useFetchActionbarAuth('MST2020_R01');

	const getParams = (
		filter: MST2020_R01Filter,
	): Partial<MST2020_R01Filter> => ({
		areaCd: filter.areaCd || undefined,
		customCd: filter.customCd || undefined,
		customNm: filter.customNm || undefined,
	});

	const isSameFilter = (
		a: MST2020_R01Filter,
		b: MST2020_R01Filter,
	) =>
		a.areaCd === b.areaCd
		&& a.customCd === b.customCd
		&& a.customNm === b.customNm;

	const renewalAllCount = useCallback(async () => {
		const res = await getMST2020_R01AllCount(
			getParams(MST2020_R01Filter),
		);
		setAllCount(res.data.data);
		return res.data.data;
	}, [MST2020_R01Filter, setAllCount]);

	const validateRow = (
		data: MST2020_R01Dto,
		currentRowIndex: number,
	) => {
		if (!validateString(data.customCd)) {
			showWarning('거래처코드를 입력해주세요.');
			return false;
		}

		const amounts: [keyof MST2020_R01Dto, string][] = [
			['purchaseAmt', '매입액'],
			['salesAmt', '매출액'],
			['receiptAmt', '수금액'],
			['paymentAmt', '지불액'],
		];

		for (const [field, label] of amounts) {
			if (Number.isNaN(Number(data[field]))) {
				showWarning(`${label}를(을) 숫자로 입력해주세요.`);
				return false;
			}
		}

		const activeRows = MST2020_R01List.filter(
			(row) =>
				!deletedMST2020_R01List.includes(
					row.valueIndex.toString(),
				),
		);

		const otherRows = activeRows.filter(
			(_, index) => index !== currentRowIndex,
		);

		if (otherRows.some((row) => row.customCd === data.customCd)) {
			showWarning(`거래처코드가 중복됩니다. (${data.customCd})`);
			return false;
		}

		return true;
	};

	const executeDataFetch = useCallback(
		async (
			fetchType: 'search' | 'refetch',
			options?: FetchOptions,
		) => {
			const messageMode = options?.messageMode ?? 'show';

			setLoadingSpinner(true);

			const same = isSameFilter(
				MST2020_R01Filter,
				MST2020_R01PastFilter,
			);

			if (!same || fetchType === 'refetch') {
				setScrollTop(null);
			}

			if (fetchType === 'refetch' || !same) {
				setMST2020_R01List([]);
			}

			const res = await getMST2020_R01List(
				'search',
				getParams(MST2020_R01Filter),
				MST2020_R01List.length ?? 0,
			);

			const newList = [...res.data.data] as MST2020_R01Dto[];

			setMST2020_R01List(newList);

			const processAllCount = await renewalAllCount();

			setOriginalMST2020_R01List(newList);
			setSelectedMST2020_R01List([]);
			removeDeletedMST2020_R01List(deletedMST2020_R01List);

			if (newList.length === 0) {
				if (messageMode === 'show') {
					showWarning('조회 결과가 없습니다.');
				}
			} else {
				setMST2020_R01PastFilter(MST2020_R01Filter);

				if (messageMode === 'show') {
					ShowSuccessFetch(
						setMessage,
						`거래처별 기초잔액 조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`,
					);
				}
			}

			setLoadingSpinner(false);

			if (messageMode === 'show') {
				setTimeout(() => setMessage(''), 5000);
			}
		},
		[
			MST2020_R01Filter,
			MST2020_R01PastFilter,
			MST2020_R01List,
			deletedMST2020_R01List,
			setMessage,
			setLoadingSpinner,
			setOriginalMST2020_R01List,
			setSelectedMST2020_R01List,
			setMST2020_R01List,
			removeDeletedMST2020_R01List,
			renewalAllCount,
			setMST2020_R01PastFilter,
			setScrollTop,
		],
	);

	const fetchData = useCallback(async () => {
		await executeDataFetch('search');
	}, [executeDataFetch]);

	const defaultMST2020_R01Row = useCallback(
		() => ({
			...defaultMST2020_R01Dto,
			inserted: true,
		}),
		[],
	);

	const handleInsertClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			InsertRowHandler({
				event: e,
				dataList: MST2020_R01List,
				rowData: {
					...defaultMST2020_R01Row(),
					valueIndex: -1,
				},
				insertRow: insertMST2020_R01List,
				defaultRowData: defaultMST2020_R01Row(),
				setClickable,
			});
		},
		[
			MST2020_R01List,
			insertMST2020_R01List,
			defaultMST2020_R01Row,
		],
	);

	const handleSaveClick = useCallback(async () => {
		const insertedList = MST2020_R01List.filter(
			(row) =>
				!deletedMST2020_R01List.includes(
					row.valueIndex.toString(),
				)
				&& row.inserted === true,
		);

		const modifiedList = MST2020_R01List.filter(
			(row) =>
				!deletedMST2020_R01List.includes(
					row.valueIndex.toString(),
				)
				&& row.isModified
				&& !row.inserted,
		);

		let valid = true;

		modifiedList.forEach((value) => {
			const index = MST2020_R01List.findIndex(
				(row) => row.valueIndex === value.valueIndex,
			);

			if (!validateRow(value, index)) {
				valid = false;
			}
		});

		if (!valid) return;

		insertedList.forEach((value) => {
			const index = MST2020_R01List.findIndex(
				(row) => row.valueIndex === value.valueIndex,
			);

			if (!validateRow(value, index)) {
				valid = false;
			}
		});

		if (!valid) return;

		const deleteStringList = deletedMST2020_R01List.map(
			(item) => item.toString(),
		);

		const deleteTargetList = MST2020_R01List.filter((item) =>
			deleteStringList.includes(item.valueIndex.toString()),
		);

		const [insRes, modRes, delRes] = await Promise.all([
			insertedList.length > 0
				? postMST2020_R01(insertedList)
				: Promise.resolve(undefined),
			modifiedList.length > 0
				? patchMST2020_R01(modifiedList)
				: Promise.resolve(undefined),
			deletedMST2020_R01List.length > 0
				? Promise.all(
					deleteTargetList.map((value) =>
						deleteMST2020_R01(value),
					),
				)
				: Promise.resolve(undefined),
		]);

		if (insRes?.data.successFlag || modRes?.data.successFlag) {
			showSuccess('기초잔액 등록·수정이 완료되었습니다.');
			setMessage('기초잔액 등록·수정이 완료되었습니다.');
		} else if (delRes) {
			showSuccess('기초잔액 삭제가 완료되었습니다.');
			setMessage('기초잔액 삭제가 완료되었습니다.');
		}

		setTimeout(() => setMessage(''), 5000);

		await executeDataFetch('refetch', {
			messageMode: 'none',
		});
	}, [
		MST2020_R01List,
		deletedMST2020_R01List,
		executeDataFetch,
		setMessage,
	]);

	const handleDeleteClick = useCallback(() => {
		if (deletedMST2020_R01List.length > 0) {
			removeDeletedMST2020_R01List(
				deletedMST2020_R01List,
			);
			removeSelectedMST2020_R01List();
			setMST2020_R01List([...MST2020_R01List]);
		} else if (selectedMST2020_R01List.length > 0) {
			addDeletedMST2020_R01List(
				selectedMST2020_R01List,
			);
			removeSelectedMST2020_R01List(
				selectedMST2020_R01List,
			);
		}
	}, [
		selectedMST2020_R01List,
		deletedMST2020_R01List,
		addDeletedMST2020_R01List,
		removeDeletedMST2020_R01List,
		removeSelectedMST2020_R01List,
		setMST2020_R01List,
		MST2020_R01List,
	]);

	return {
		clickable,
		setClickable,
		fetchData,
		defaultMST2020_R01Row,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
	};
};
